import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { useDemoState } from "@/data/demoState";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Calendar as CalIcon, ChevronLeft, ChevronRight, Clock, AlertTriangle,
  Truck, Lock, Send, CheckCircle2, Sparkles, Zap,
  ArrowRight, Users, DollarSign, Eye, Target
} from "lucide-react";

const eventTypeConfig = {
  'trade-release': { color: 'bg-blue-600', label: 'Trade', textColor: 'text-blue-700', bgLight: 'bg-blue-50', borderColor: 'border-blue-200', icon: Truck },
  'blocker-followup': { color: 'bg-red-500', label: 'Chase', textColor: 'text-red-700', bgLight: 'bg-red-50', borderColor: 'border-red-200', icon: AlertTriangle },
  'consultant-due': { color: 'bg-amber-500', label: 'Due', textColor: 'text-amber-700', bgLight: 'bg-amber-50', borderColor: 'border-amber-200', icon: Clock },
  'proof-review': { color: 'bg-emerald-500', label: 'Review', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50', borderColor: 'border-emerald-200', icon: Eye },
  'surveyor-ready': { color: 'bg-indigo-600', label: 'Surveyor', textColor: 'text-indigo-700', bgLight: 'bg-indigo-50', borderColor: 'border-indigo-200', icon: CheckCircle2 },
  'internal-review': { color: 'bg-slate-500', label: 'Internal', textColor: 'text-slate-600', bgLight: 'bg-slate-50', borderColor: 'border-slate-200', icon: Users },
  'owner-supplied': { color: 'bg-purple-500', label: 'Owner', textColor: 'text-purple-700', bgLight: 'bg-purple-50', borderColor: 'border-purple-200', icon: Users },
};

const urgencyConfig = {
  critical: { bg: 'bg-red-500', text: 'text-white', label: 'Critical', ring: 'ring-red-200' },
  high: { bg: 'bg-amber-500', text: 'text-white', label: 'High', ring: 'ring-amber-200' },
  medium: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Medium', ring: 'ring-blue-100' },
  low: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Low', ring: 'ring-slate-100' },
  warning: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Warning', ring: 'ring-amber-100' },
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const TODAY_KEY = '2025-01-20';

function getMonthDays(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const days = [];
  let startDay = first.getDay() === 0 ? 6 : first.getDay() - 1;
  for (let i = startDay - 1; i >= 0; i--) { const d = new Date(year, month, -i); days.push({ date: d, isCurrentMonth: false }); }
  for (let i = 1; i <= last.getDate(); i++) { days.push({ date: new Date(year, month, i), isCurrentMonth: true }); }
  const rem = 42 - days.length;
  for (let i = 1; i <= rem; i++) { days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false }); }
  return days;
}

function fmtKey(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }

function CalendarDayCell({ day, events, isToday, onSelect }) {
  const hasCritical = events.some(e => e.urgency === 'critical');
  return (
    <div className={`min-h-[110px] p-1.5 border-r border-b border-slate-100 last:border-r-0 transition-colors ${!day.isCurrentMonth ? 'bg-slate-50/30' : 'bg-white'} ${isToday ? 'bg-blue-50/40 ring-1 ring-inset ring-blue-200' : ''} hover:bg-slate-50/50`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[11px] font-semibold ${isToday ? 'text-blue-600 bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center' : day.isCurrentMonth ? 'text-slate-700' : 'text-slate-300'}`}>
          {day.date.getDate()}
        </span>
        {hasCritical && !isToday && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
      </div>
      <div className="space-y-0.5">
        {events.slice(0, 3).map(ev => {
          const cfg = eventTypeConfig[ev.type] || eventTypeConfig['internal-review'];
          return (
            <button key={ev.id} onClick={() => onSelect(ev)}
              className={`w-full text-left px-1.5 py-1 rounded-md ${cfg.bgLight} hover:ring-1 ${urgencyConfig[ev.urgency]?.ring || 'ring-slate-200'} transition-all cursor-pointer group`}
              data-testid={`cal-event-${ev.id}`}>
              <div className="flex items-center gap-1">
                <span className={`w-1.5 h-1.5 rounded-full ${cfg.color} shrink-0`} />
                <span className={`text-[9px] font-semibold ${cfg.textColor} truncate group-hover:text-slate-900`}>{ev.title}</span>
              </div>
            </button>
          );
        })}
        {events.length > 3 && <p className="text-[8px] text-slate-400 pl-1 font-medium">+{events.length - 3} more</p>}
      </div>
    </div>
  );
}

function AgendaItem({ event, onSelect }) {
  const cfg = eventTypeConfig[event.type] || eventTypeConfig['internal-review'];
  const urg = urgencyConfig[event.urgency] || urgencyConfig.medium;
  const Icon = cfg.icon;
  return (
    <button onClick={() => onSelect(event)}
      className={`w-full text-left p-4 rounded-xl border-l-4 bg-white border border-slate-200 hover:shadow-md transition-all group`}
      style={{ borderLeftColor: cfg.color.replace('bg-', '').includes('blue') ? '#2563eb' : cfg.color.replace('bg-', '').includes('red') ? '#ef4444' : cfg.color.replace('bg-', '').includes('amber') ? '#f59e0b' : cfg.color.replace('bg-', '').includes('emerald') ? '#10b981' : cfg.color.replace('bg-', '').includes('indigo') ? '#4f46e5' : cfg.color.replace('bg-', '').includes('purple') ? '#9333ea' : '#64748b' }}
      data-testid={`agenda-${event.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cfg.color} shrink-0 shadow-sm`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-sm font-bold text-slate-900 truncate">{event.title}</p>
            <Badge className={`text-[9px] ${urg.bg} ${urg.text} shrink-0`}>{urg.label}</Badge>
          </div>
          <p className="text-xs text-slate-500 mb-1">{event.project} · {event.stage || 'General'}</p>
          <p className="text-[11px] text-slate-600 line-clamp-2">{event.description}</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-[10px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {event.date} {event.time}</span>
            {event.trade && <span className="text-[10px] text-slate-400 flex items-center gap-1"><Truck className="w-3 h-3" /> {event.trade}</span>}
            {event.linkedBlockerId && <span className="text-[10px] text-red-500 flex items-center gap-1"><Lock className="w-3 h-3" /> Blocker linked</span>}
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0 mt-2 transition-colors" />
      </div>
    </button>
  );
}

export default function ReleaseCalendar() {
  const { calendarEvents, blockers, trades, sendChase } = useDemoState();
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date(2025, 0, 20));
  const [selected, setSelected] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthDays = useMemo(() => getMonthDays(year, month), [year, month]);
  const eventMap = useMemo(() => {
    const m = {};
    calendarEvents.forEach(e => { if (!m[e.date]) m[e.date] = []; m[e.date].push(e); });
    return m;
  }, [calendarEvents]);

  const weekStart = new Date(2025, 0, 20);
  const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });

  const sortedEvents = useMemo(() => {
    const urgencyOrder = { critical: 0, high: 1, warning: 2, medium: 3, low: 4 };
    return [...calendarEvents].sort((a, b) => {
      const ua = urgencyOrder[a.urgency] ?? 3;
      const ub = urgencyOrder[b.urgency] ?? 3;
      return ua !== ub ? ua - ub : a.date.localeCompare(b.date);
    });
  }, [calendarEvents]);

  // AI insights
  const aiInsights = useMemo(() => {
    const s = [];
    const criticalEvents = calendarEvents.filter(e => e.urgency === 'critical');
    const blockerFollowups = calendarEvents.filter(e => e.type === 'blocker-followup');
    const tradeWindows = calendarEvents.filter(e => e.type === 'trade-release' && e.urgency === 'critical');
    if (criticalEvents.length > 0) s.push({ icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', text: `${criticalEvents.length} critical event(s) this period. Prioritise these today.` });
    blockerFollowups.forEach(e => {
      const blk = blockers.find(b => b.id === e.linkedBlockerId);
      if (blk) s.push({ icon: Send, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: `Chase ${blk.owner} today — "${blk.title}" follow-up scheduled.` });
    });
    tradeWindows.forEach(e => s.push({ icon: DollarSign, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', text: `${e.title} — missing this window triggers re-mobilisation costs.` }));
    const clearableBlockers = blockers.filter(b => b.status === 'under-review');
    clearableBlockers.forEach(b => {
      const affectedTrades = trades.filter(t => t.blockerIds.includes(b.id) && t.status === 'hold');
      if (affectedTrades.length) s.push({ icon: Zap, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: `If "${b.title}" clears, ${affectedTrades.map(t => t.trade).join(' + ')} may unlock.` });
    });
    return s;
  }, [calendarEvents, blockers, trades]);

  // Event type counts for header
  const typeCounts = useMemo(() => {
    const counts = {};
    calendarEvents.forEach(e => { counts[e.type] = (counts[e.type] || 0) + 1; });
    return counts;
  }, [calendarEvents]);

  const selCfg = selected ? (eventTypeConfig[selected.type] || eventTypeConfig['internal-review']) : null;
  const selBlocker = selected?.linkedBlockerId ? blockers.find(b => b.id === selected.linkedBlockerId) : null;
  const selTrade = selected?.trade ? trades.find(t => t.trade === selected.trade) : null;

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="release-calendar-page">
        {/* Quick stats header */}
        <div className="flex items-center gap-3 flex-wrap" data-testid="calendar-type-legend">
          {Object.entries(eventTypeConfig).map(([key, cfg]) => {
            const count = typeCounts[key] || 0;
            if (count === 0) return null;
            return (
              <div key={key} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ${cfg.bgLight} border ${cfg.borderColor}`}>
                <span className={`w-2 h-2 rounded-full ${cfg.color}`} />
                <span className={`text-[10px] font-semibold ${cfg.textColor}`}>{cfg.label}</span>
                <span className="text-[9px] text-slate-400 font-medium">{count}</span>
              </div>
            );
          })}
          <div className="ml-auto">
            <Badge className="text-[10px] bg-slate-900 text-white">{calendarEvents.length} events total</Badge>
          </div>
        </div>

        {/* AI Calendar Intelligence */}
        {aiInsights.length > 0 && (
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-900 rounded-xl p-4 text-white" data-testid="calendar-ai-panel">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <span className="text-xs font-bold text-white">Calendar Intelligence</span>
                <span className="text-[9px] text-slate-400 ml-2">Readiness-aware scheduling</span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {aiInsights.slice(0, 4).map((s, i) => (
                <div key={i} className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${s.border} ${s.bg}`}>
                  <s.icon className={`w-3.5 h-3.5 ${s.color} shrink-0 mt-0.5`} />
                  <span className="text-[11px] text-slate-200 leading-relaxed">{s.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View tabs + navigation */}
        <div className="flex items-center justify-between">
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="bg-slate-100 h-9" data-testid="calendar-view-tabs">
              <TabsTrigger value="month" className="text-xs px-5 font-semibold">Month</TabsTrigger>
              <TabsTrigger value="week" className="text-xs px-5 font-semibold">Week</TabsTrigger>
              <TabsTrigger value="agenda" className="text-xs px-5 font-semibold">Agenda</TabsTrigger>
            </TabsList>
          </Tabs>
          {view === 'month' && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={prevMonth} className="h-8 w-8 p-0"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm font-bold text-slate-800 min-w-[150px] text-center">{MONTHS[month]} {year}</span>
              <Button variant="outline" size="sm" onClick={nextMonth} className="h-8 w-8 p-0"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
          {view === 'week' && <span className="text-sm font-bold text-slate-800">Week of 20 January 2025</span>}
        </div>

        {/* Month view */}
        {view === 'month' && (
          <Card className="border-slate-200 overflow-hidden shadow-sm" data-testid="month-view">
            <div className="grid grid-cols-7 bg-slate-900">
              {DAYS.map(d => <div key={d} className="text-center py-2.5 text-[10px] font-bold uppercase text-slate-300 tracking-wider">{d}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {monthDays.map((day, i) => {
                const key = fmtKey(day.date);
                const dayEvents = eventMap[key] || [];
                const isToday = key === TODAY_KEY;
                return <CalendarDayCell key={i} day={day} events={dayEvents} isToday={isToday} onSelect={setSelected} />;
              })}
            </div>
          </Card>
        )}

        {/* Week view */}
        {view === 'week' && (
          <Card className="border-slate-200 overflow-hidden shadow-sm" data-testid="week-view">
            <div className="divide-y divide-slate-100">
              {weekDays.map(d => {
                const key = fmtKey(d);
                const dayEvents = eventMap[key] || [];
                const isToday = key === TODAY_KEY;
                const dayName = d.toLocaleDateString('en-AU', { weekday: 'short' });
                const dayNum = d.getDate();
                const monthName = d.toLocaleDateString('en-AU', { month: 'short' });
                return (
                  <div key={key} className={`flex ${isToday ? 'bg-blue-50/30' : ''}`}>
                    <div className={`w-20 shrink-0 py-3 px-3 border-r border-slate-100 ${isToday ? 'bg-blue-600' : 'bg-slate-50'}`}>
                      <p className={`text-xl font-heading font-bold ${isToday ? 'text-white' : 'text-slate-800'}`}>{dayNum}</p>
                      <p className={`text-[10px] font-semibold ${isToday ? 'text-blue-100' : 'text-slate-400'}`}>{dayName}</p>
                      <p className={`text-[9px] ${isToday ? 'text-blue-200' : 'text-slate-300'}`}>{monthName}</p>
                    </div>
                    <div className="flex-1 p-2.5 space-y-1.5">
                      {dayEvents.map(ev => {
                        const cfg = eventTypeConfig[ev.type] || eventTypeConfig['internal-review'];
                        const urg = urgencyConfig[ev.urgency] || urgencyConfig.medium;
                        const Icon = cfg.icon;
                        return (
                          <button key={ev.id} onClick={() => setSelected(ev)}
                            className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border ${cfg.borderColor} ${cfg.bgLight} hover:shadow-md transition-all`}
                            data-testid={`week-event-${ev.id}`}>
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cfg.color} shrink-0 shadow-sm`}>
                              <Icon className="w-3.5 h-3.5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-semibold text-slate-900 truncate">{ev.title}</p>
                                <Badge className={`text-[8px] ${urg.bg} ${urg.text} shrink-0`}>{urg.label}</Badge>
                              </div>
                              <p className="text-[10px] text-slate-500 truncate">{ev.project} · {ev.time}</p>
                            </div>
                            {ev.linkedBlockerId && <Lock className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                          </button>
                        );
                      })}
                      {dayEvents.length === 0 && <p className="text-xs text-slate-300 py-3 px-2 italic">No events</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Agenda view */}
        {view === 'agenda' && (
          <div className="space-y-2.5" data-testid="agenda-view">
            {sortedEvents.length > 0 ? sortedEvents.map(ev => (
              <AgendaItem key={ev.id} event={ev} onSelect={setSelected} />
            )) : (
              <Card className="border-slate-200"><CardContent className="py-8 text-center text-sm text-slate-400">No events</CardContent></Card>
            )}
          </div>
        )}

        {/* Detail drawer */}
        <Sheet open={!!selected} onOpenChange={() => setSelected(null)}>
          <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="calendar-detail-drawer">
            {selected && selCfg && (
              <>
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2 text-base">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selCfg.color} shadow-sm`}>
                      <selCfg.icon className="w-4 h-4 text-white" />
                    </div>
                    {selected.title}
                  </SheetTitle>
                  <SheetDescription>{selected.project} · {selected.date} at {selected.time}</SheetDescription>
                </SheetHeader>

                <div className="mt-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] text-slate-400 font-semibold uppercase mb-1">Type</p>
                      <Badge className={`${selCfg.bgLight} ${selCfg.textColor} border ${selCfg.borderColor} text-xs`}>{selCfg.label}</Badge>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] text-slate-400 font-semibold uppercase mb-1">Urgency</p>
                      <Badge className={`${urgencyConfig[selected.urgency]?.bg} ${urgencyConfig[selected.urgency]?.text} text-xs`}>{urgencyConfig[selected.urgency]?.label}</Badge>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] text-slate-400 font-semibold uppercase mb-1">Description</p>
                    <p className="text-xs text-slate-700 leading-relaxed">{selected.description}</p>
                  </div>

                  {selected.stage && (
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] text-slate-400 font-semibold uppercase mb-1">Stage</p>
                      <p className="text-xs text-slate-700">{selected.stage}</p>
                    </div>
                  )}

                  {selBlocker && (
                    <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                      <p className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Linked blocker</p>
                      <p className="text-xs text-red-700 font-medium">{selBlocker.title}</p>
                      <p className="text-[10px] text-red-600">Owner: {selBlocker.owner} · Due: {selBlocker.dueDate} · {selBlocker.severity}</p>
                      <Button size="sm" variant="outline" className="text-[10px] h-6 border-red-200 text-red-600 hover:bg-red-100 mt-2"
                        onClick={() => { sendChase(selBlocker.id, 'email'); toast.success('Chase sent'); }}>
                        <Send className="w-2.5 h-2.5 mr-1" /> Chase {selBlocker.owner}
                      </Button>
                    </div>
                  )}

                  {selTrade && (
                    <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                      <p className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Linked trade</p>
                      <p className="text-xs text-blue-700 font-medium">{selTrade.trade} — {selTrade.company}</p>
                      <p className="text-[10px] text-blue-600">Status: {selTrade.status} · Contact: {selTrade.contact}</p>
                    </div>
                  )}

                  <div className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-indigo-600" />
                      <span className="text-xs font-semibold text-indigo-700">What should happen</span>
                    </div>
                    <p className="text-xs text-indigo-700 leading-relaxed">
                      {selected.type === 'blocker-followup' && selBlocker ? `Send follow-up to ${selBlocker.owner}. If resolved, this unblocks ${trades.filter(t => t.blockerIds.includes(selBlocker.id)).length} trade(s).` :
                       selected.type === 'trade-release' ? `Confirm trade availability with ${selTrade?.contact || 'contact'}. Ensure site access. Check readiness gate.` :
                       selected.type === 'consultant-due' ? `Verify deliverable received. If not, escalate. This deadline affects stage readiness.` :
                       selected.type === 'proof-review' ? `Complete evidence review. Approved proof clears the linked condition and may unlock the stage gate.` :
                       `Review and take required action. Check linked items for dependencies.`}
                    </p>
                  </div>

                  <Separator />
                  <div className="flex gap-2 flex-wrap">
                    <Link to="/builder/trade-release"><Button variant="outline" size="sm" className="text-xs"><Truck className="w-3.5 h-3.5 mr-1" /> Trade board</Button></Link>
                    <Link to="/builder/stage-readiness"><Button variant="outline" size="sm" className="text-xs"><Target className="w-3.5 h-3.5 mr-1" /> Readiness</Button></Link>
                    <Link to="/builder/job-blocker"><Button variant="outline" size="sm" className="text-xs"><AlertTriangle className="w-3.5 h-3.5 mr-1" /> Blockers</Button></Link>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>
  );
}
